import { Breadcrumb } from "@app/shared/components/Breadcrumb";
import { type BreadcrumbItem } from "@core/shared/types";
import { formatLocalizedNumber } from "@core/shared/utils";
import { trans } from "@mongez/localization";
import {
  BookOpenCheck,
  FolderOpen,
  GraduationCap,
  Sparkles,
} from "lucide-react";

type LessonsCatalogHeroProps = {
  breadcrumbs: BreadcrumbItem[];
  title: React.ReactNode;
  description: React.ReactNode;
  totalLessons: number;
  visibleLessons: number;
  categoriesCount: number;
};

export function LessonsCatalogHero({
  breadcrumbs,
  title,
  description,
  totalLessons,
  visibleLessons,
  categoriesCount,
}: LessonsCatalogHeroProps) {
  const highlights = [
    {
      icon: BookOpenCheck,
      value: totalLessons,
      label: trans("lessonCatalog.totalLibrary"),
      className: "bg-white/15 text-white",
    },
    {
      icon: FolderOpen,
      value: categoriesCount,
      label: trans("lessonCatalog.visibleCategories"),
      className: "bg-cyan-300/20 text-cyan-50",
    },
    {
      icon: GraduationCap,
      value: visibleLessons,
      label: trans("lessonCatalog.currentResults"),
      className: "bg-amber-300/20 text-amber-50",
    },
  ];

  return (
    <section className="shadow-primary-950/10 relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-indigo-950 via-primary-700 to-violet-600 px-5 py-6 text-white shadow-xl sm:px-8 sm:py-8 lg:px-10">
      <div className="absolute -start-16 -top-20 h-52 w-52 rounded-full bg-cyan-300/20 blur-3xl" />
      <div className="absolute -bottom-24 end-0 h-64 w-64 rounded-full bg-fuchsia-300/20 blur-3xl" />
      <div className="absolute end-8 top-8 hidden rotate-12 rounded-3xl border border-white/10 bg-white/5 p-8 lg:block">
        <BookOpenCheck className="size-20 text-white/20" />
      </div>

      <div className="relative z-10">
        <div className="mb-5 [&_*]:!text-white/75">
          <Breadcrumb items={breadcrumbs} />
        </div>

        <div className="max-w-3xl">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold backdrop-blur-sm sm:text-sm">
            <Sparkles className="size-4 text-amber-300" />
            {trans("lessonCatalog.discoveryBadge")}
          </div>
          <h1 className="text-3xl font-black leading-tight sm:text-4xl lg:text-5xl">
            {title}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-indigo-50/85 sm:text-base">
            {description}
          </p>
        </div>

        <div className="mt-7 grid gap-2 sm:grid-cols-3 sm:gap-3 lg:max-w-3xl">
          {highlights.map(({ icon: Icon, value, label, className }) => (
            <div
              className={`flex items-center gap-3 rounded-2xl border border-white/10 px-3 py-3 backdrop-blur-sm ${className}`}
              key={label}
            >
              <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-white/10">
                <Icon className="size-5" />
              </span>
              <span>
                <strong className="block text-xl font-black leading-none">
                  {formatLocalizedNumber(value)}
                </strong>
                <span className="mt-1 block text-[11px] font-medium opacity-80 sm:text-xs">
                  {label}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
